import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CopilotAssistant } from '@/hooks/ai-agents/types';
import { cn } from '@/lib/utils';
import { Settings } from 'lucide-react';

interface HeaderContentProps {
  activeAssistant: CopilotAssistant | null;
  isCompact: boolean;
}

const HeaderContent: React.FC<HeaderContentProps> = ({ 
  activeAssistant,
  isCompact 
}) => {
  // Common animation settings
  const transitionSettings = {
    type: "spring",
    stiffness: 120,
    damping: 20,
    mass: 1.2
  };

  // Don't render any content if no assistant is loaded
  if (!activeAssistant) return null;

  return (
    <motion.div 
      className={cn(
        isCompact ? "flex-1" : "text-center"
      )}
      layoutId="text-content"
    >
      <motion.h3 
        className={cn(
          "font-semibold bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 bg-clip-text text-transparent",
          isCompact ? "text-lg" : "text-2xl mb-3"
        )}
        layoutId="assistant-name"
      >
        {activeAssistant.name}
      </motion.h3>
      
      {/* Description - Only show in expanded mode */}
      <AnimatePresence mode="popLayout">
        {!isCompact && activeAssistant.description && (
          <motion.div 
            key="description"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { 
                ...transitionSettings,
                delay: 0.15
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10, 
              scale: 0.95,
              transition: {
                ...transitionSettings,
                duration: 0.35
              }
            }}
            className="mb-5 mx-auto max-w-[230px] p-2 rounded-lg bg-white/50 dark:bg-slate-800/50 
            backdrop-blur-sm border border-white/20 dark:border-slate-700/20 shadow-sm"
          >
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              {activeAssistant.specialization}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Settings Button - Show in both modes, but styled differently */}
      <AnimatePresence mode="popLayout">
        {isCompact ? (
          <motion.div
            key="compact-settings"
            className="absolute right-3 top-0 h-full flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{
              type: "spring",
              stiffness: 120,
              damping: 20,
              mass: 1.2
            }}
          >
            <Link 
              to="/account/ai-agents" 
              className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-blue-100/40 to-purple-100/40 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-500/80 dark:text-blue-400/80 hover:text-blue-600 dark:hover:text-blue-300 backdrop-blur-sm border border-white/20 dark:border-white/5 hover:border-blue-200/50 dark:hover:border-blue-500/30 group-hover:shadow-sm transition-all duration-200"
              aria-label="Agent Settings"
            >
              <Settings size={14} className="stroke-[1.75]" />
            </Link>
          </motion.div>
        ) : (
          <SettingsSection />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SettingsSection = React.forwardRef<HTMLDivElement>((props, ref) => {
  // Common animation settings
  const transitionSettings = {
    type: "spring",
    stiffness: 120,
    damping: 20,
    mass: 1.2
  };

  return (
    <motion.div 
      ref={ref}
      key="settings"
      className="absolute top-3 right-3 z-10"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        transition: {
          ...transitionSettings,
          delay: 0.25
        }
      }}
      exit={{ 
        opacity: 0, 
        scale: 0.9,
        transition: {
          ...transitionSettings,
          duration: 0.35 
        }
      }}
    >
      <Link to="/account/ai-agents" className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r from-blue-100/40 to-purple-100/40 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-500/80 dark:text-blue-400/80 hover:text-blue-600 dark:hover:text-blue-300 backdrop-blur-sm border border-white/20 dark:border-white/5 hover:border-blue-200/50 dark:hover:border-blue-500/30 group-hover:shadow-sm transition-all duration-200" aria-label="Agent Settings">
        <Settings size={16} className="stroke-[1.75]" />
      </Link>
    </motion.div>
  );
});

SettingsSection.displayName = 'SettingsSection';

export default HeaderContent;
